import { useCallback } from 'react'
import { Properties } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'
import ChannelMembersField from './ChannelMembersField'

/**
 * THE CHANNEL'S PROPERTY LEDGER — synapse's half of the shell's <Properties>.
 *
 * The shell owns the grammar, the type vocabulary and the editing. This owns
 * the two things only synapse can know: what a channel's own fields MEAN (a
 * members row is the roster itself, edited in place; a created row is a date
 * in this app's voice) and where the ledger is kept (ChatContext →
 * PUT /properties → every member's socket).
 *
 * DEFAULT: members alone. A channel's identity is who is in it; created and
 * last message are inspector trivia that were only ever shown because a
 * hardcoded list had no way to say "not this one". The default lives HERE and
 * not in the database, so the day it changes, every channel that never made a
 * choice changes with it. A channel that HAS made a choice stores the whole
 * hidden list and is never second-guessed.
 */
/* ALL DEFAULTS STAND (owner call 2026-08-22): with the ledger behind the
   corner plate instead of a permanent band, showing everything costs no
   surface — description (when one exists), members, created and last
   message are all there by default; a channel can still say no per row. */
const DEFAULT_HIDDEN = []

export default function ChannelProperties({
  channel,
  members = [],
  lastMessageStamp,
  avatarColor,
  formatTime,
  className,
}) {
  const { setChannelProperties } = useChat()

  const properties = channel?.properties || []
  // null means "never chose" — the app's default answers. An empty array is a
  // real choice (show everything) and must survive a reload.
  // MIGRATION (2026-08-22): the old default HID description/created/last
  // message, and some channels persisted that exact triple without anyone
  // choosing it. That precise set reads as "never chose" too — a user who
  // hand-hid rows almost never lands on exactly the legacy default.
  const stored = channel?.properties_hidden
  const isLegacyDefault = Array.isArray(stored) && stored.length === 3
    && ['description', 'created', 'last_message'].every((k) => stored.includes(k))
  const hidden = (stored == null || isLegacyDefault) ? DEFAULT_HIDDEN : stored

  const save = useCallback((nextProperties, nextHidden) => {
    if (!channel?.id) return
    setChannelProperties(channel.id, nextProperties, nextHidden)
  }, [channel?.id, setChannelProperties])

  if (!channel) return null

  const date = (stamp) => new Date(stamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  const fields = [
    channel.description && {
      key: 'description',
      label: 'description',
      glyph: '≡',
      children: channel.description,
    },
    {
      key: 'members',
      label: 'members',
      glyph: '⌾',
      children: <ChannelMembersField channel={channel} members={members} avatarColor={avatarColor} />,
    },
    channel.created_at && {
      key: 'created',
      label: 'created',
      glyph: '☷',
      children: date(channel.created_at),
    },
    lastMessageStamp && {
      key: 'last_message',
      label: 'last message',
      glyph: '☷',
      children: `${date(lastMessageStamp)} · ${formatTime ? formatTime(lastMessageStamp) : ''}`,
    },
  ].filter(Boolean)

  return (
    <Properties
      className={className}
      fields={fields}
      hidden={hidden}
      onHiddenChange={(next) => save(properties, next)}
      properties={properties}
      onPropertiesChange={(next) => save(next, hidden)}
    />
  )
}
